import { useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import { useClubMembership } from '../hooks/useClubMembership'
import { rememberSignInReturn } from '../lib/postSignInReturn'
import { usePageTitle } from '../hooks/usePageTitle'
import {
  ClubButton,
  ClubLinkButton,
  ClubPage,
  Container,
  EmptyPlate,
  Eyebrow,
  JoinCard,
} from '../components/golf'
import { MagicLinkSignIn } from '../components/MagicLinkSignIn'
import type {
  QuartermasterAcceptResponse,
  QuartermasterInviteView,
  QuartermasterInviteViewResponse,
} from '../lib/types'

/**
 * A Quartermaster invitation (`/join/quartermaster/:token`) — PUBLIC and
 * tokenized like the outing invite page, but with one more step in it: a
 * standing lands on an ACCOUNT, so accepting requires a session.
 *
 *   signed out → the invitation, and the house magic-link card. The page
 *     records itself via postSignInReturn, so the sign-in that starts here
 *     ends here (same device; cross-device lands on /clubhouse and the
 *     emailed link finishes the errand).
 *   signed in  → the invitation and ONE accept CTA (the page's single flag),
 *     then a confirmed state that opens the clubhouse.
 *
 * It wears the `invite` chrome — the public header without its Apply pill,
 * over the full foot — so whichever state is showing, that state's CTA is the
 * one flag on the page; `public` would spend a second on Apply for every
 * signed-out invitee (see `ClubPage`).
 *
 * Dead tokens — unknown and revoked alike — arrive as one uniform 404 by
 * design, and render one honest dead state. The 409s (withdrawn mid-visit,
 * taken by another account) only surface at accept, and each gets its own
 * plain sentence.
 */

type DeadReason = 'gone' | 'revoked' | 'spent'

const DEAD_COPY: Record<DeadReason, { title: string; description: string }> = {
  gone: {
    title: "That invitation isn't in the book.",
    description:
      'The link may be mistyped, or the invitation it carried is no longer on the books. ' +
      'If it was meant for you, ask the club for a fresh one.',
  },
  revoked: {
    title: 'The club has withdrawn this invitation.',
    description:
      'A granted standing is the club’s to give and the club’s to withdraw. ' +
      'If you believe this is a mistake, write to the club.',
  },
  spent: {
    title: 'This invitation has been taken up.',
    description:
      'One invitation seats one member, and another account has already accepted this one. ' +
      'If that was you under a different address, sign in with it.',
  },
}

export function QuartermasterInviteScreen({ token }: { token: string }) {
  usePageTitle('A standing at the club')

  const { isAuthenticated } = useAuth()
  const { refresh } = useClubMembership()

  const [invite, setInvite] = useState<QuartermasterInviteView | null>(null)
  const [dead, setDead] = useState<DeadReason | null>(null)
  const [accepted, setAccepted] = useState<string | null>(null) // member number, once granted
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    api
      .get<QuartermasterInviteViewResponse>(`/quartermaster_invites/${encodeURIComponent(token)}`)
      .then((data) => {
        if (active) setInvite(data.invite)
      })
      .catch(() => {
        // Unknown and revoked read the same on purpose (uniform 404): no live
        // invitation behind this link.
        if (active) setDead('gone')
      })
    return () => {
      active = false
    }
  }, [token])

  // The sign-in that starts on this page should end on this page — record the
  // errand while there is one (pending, and nobody signed in yet).
  useEffect(() => {
    if (!isAuthenticated && invite?.status === 'pending') {
      rememberSignInReturn(`/join/quartermaster/${token}`)
    }
  }, [isAuthenticated, invite, token])

  const accept = async () => {
    setBusy(true)
    setError(null)
    try {
      const data = await api.post<QuartermasterAcceptResponse>(
        `/quartermaster_invites/${encodeURIComponent(token)}/accept`,
        {},
      )
      setAccepted(data.membership.member_number ?? '')
      // The clubhouse gate reads the shared membership context; without this
      // it would still hold "no standing" and bounce the new Quartermaster
      // from the very door we are about to point them at.
      await refresh()
    } catch (e) {
      if (e instanceof ApiError && e.code === 'invite_revoked') {
        setDead('revoked')
      } else if (e instanceof ApiError && e.code === 'invite_spent') {
        setDead('spent')
      } else {
        setError(e instanceof Error ? e.message : 'That did not go through — try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <ClubPage chrome="invite">
      <Container className="max-w-[640px] pb-16 pt-16 text-center">
        {dead ? (
          <EmptyPlate
            title={DEAD_COPY[dead].title}
            description={DEAD_COPY[dead].description}
            action={
              <ClubLinkButton to={dead === 'spent' ? '/sign-in' : '/'} variant="ghost" size="sm">
                {dead === 'spent' ? 'Sign in' : 'See the club'}
              </ClubLinkButton>
            }
          />
        ) : !invite ? (
          <p className="text-[14.5px] text-ink-soft">Fetching the invitation…</p>
        ) : accepted !== null ? (
          <>
            <Eyebrow>Your standing is struck</Eyebrow>
            <h1 className="mb-3.5 mt-4 font-display text-[clamp(2rem,4.5vw,2.75rem)] font-medium leading-[1.06] tracking-[-0.014em] text-pine">
              Welcome, Quartermaster.
            </h1>
            <p className="mx-auto max-w-[46ch] text-[15.5px] text-ink">
              {accepted
                ? `The register has been amended — you are ${accepted}.`
                : 'The register has been amended.'}{' '}
              The standing is granted, with nothing to settle.
            </p>
            <div className="mt-9">
              <ClubLinkButton to="/clubhouse">Into the clubhouse</ClubLinkButton>
            </div>
          </>
        ) : invite.status === 'accepted' ? (
          // Accepted before this visit — most likely the member returning on a
          // fresh session. The clubhouse door sorts out who they are.
          <>
            <Eyebrow>A standing at the club</Eyebrow>
            <h1 className="mb-3.5 mt-4 font-display text-[clamp(2rem,4.5vw,2.75rem)] font-medium leading-[1.06] tracking-[-0.014em] text-pine">
              This invitation has been taken up.
            </h1>
            <p className="mx-auto max-w-[46ch] text-[15.5px] text-ink-soft">
              If that was you, the clubhouse is yours{isAuthenticated ? '' : ' — sign in'}.
            </p>
            <div className="mt-9">
              <ClubLinkButton to={isAuthenticated ? '/clubhouse' : '/sign-in'}>
                {isAuthenticated ? 'Into the clubhouse' : 'Sign in'}
              </ClubLinkButton>
            </div>
          </>
        ) : (
          <>
            <Eyebrow>A standing, held for you</Eyebrow>
            <h1 className="mb-3.5 mt-4 font-display text-[clamp(2rem,4.5vw,2.75rem)] font-medium leading-[1.06] tracking-[-0.014em] text-pine">
              You&rsquo;ve been invited to a standing at the club.
            </h1>
            <p className="mx-auto max-w-[46ch] text-[15.5px] text-ink">
              A Quartermaster&rsquo;s place — the full run of the club, granted outright. Nothing to
              settle, no dues to keep.
            </p>
            <p className="mx-auto mt-1.5 max-w-[46ch] text-[13.5px] text-ink-soft">
              The standing holds at the club&rsquo;s pleasure, and the club may withdraw it.
            </p>

            {isAuthenticated ? (
              <div className="mt-8">
                <ClubButton onClick={accept} disabled={busy}>
                  {busy ? 'Amending the register…' : 'Take up the standing'}
                </ClubButton>
              </div>
            ) : (
              <JoinCard className="mt-10">
                {/* The page's h1 is the invitation above; the stationery's
                    heading is its h2, as the card draws it. */}
                <MagicLinkSignIn mode="invite" headingLevel={2} />
              </JoinCard>
            )}

            {error && (
              <p role="alert" className="mt-5 text-[12.5px] text-flag">
                {error}
              </p>
            )}
          </>
        )}
      </Container>
    </ClubPage>
  )
}
